import {
  ApolloClient,
  InMemoryCache,
  NormalizedCacheObject,
  TypePolicies,
  TypePolicy,
} from "@apollo/client";
import searchTypePolicies from "search/typePolicies";
import bidTypePolicies from "bid/typePolicies";

import isSSR from "@/utils/isSSR";
import createCompositeLink from "./links/composite";

let _apolloClient: ApolloClient<NormalizedCacheObject> | undefined;

type ApolloClientParams = {
  basePath?: string;
  mfeTypePolicies?: TypePolicies[];
  subscriptionUrl: string;
  enableSubscriptionLogging?: boolean;
};

export function createApolloClient({
  basePath = "",
  subscriptionUrl,
  mfeTypePolicies = [searchTypePolicies, bidTypePolicies],
  enableSubscriptionLogging = false,
}: ApolloClientParams) {
  const mergedTypePolicies = registerTypePolicies(mfeTypePolicies);

  const apolloClient = new ApolloClient({
    link: createCompositeLink({
      basePath,
      subscriptionUrl,
      enableSubscriptionLogging,
    }),
    ssrMode: isSSR(),
    connectToDevTools: process.env.NEXT_PUBLIC_DEPLOY_ENV !== "prod",
    cache: new InMemoryCache({
      typePolicies: {
        ...mergedTypePolicies,
        Listing: {
          keyFields: ["listingId"],
          merge: true,
        },
      },
    }),
  });

  return apolloClient;
}

export function getApolloClient({
  basePath = "",
  subscriptionUrl,
  mfeTypePolicies = [searchTypePolicies, bidTypePolicies],
  enableSubscriptionLogging = false,
}: ApolloClientParams) {
  if (isSSR()) {
    return createApolloClient({
      basePath,
      mfeTypePolicies,
      subscriptionUrl,
      enableSubscriptionLogging,
    });
  }

  if (!_apolloClient) {
    _apolloClient = createApolloClient({
      basePath,
      mfeTypePolicies,
      subscriptionUrl,
      enableSubscriptionLogging,
    });
  }

  return _apolloClient;
}

const registerTypePolicies = (typePolicies: TypePolicies[]) => {
  const typePolicyRegistry = typePolicies.reduce((acc, typePolicy) => {
    Object.entries(typePolicy).forEach(([typeName, mfeTypePolicy]) => {
      const registeredTypePolicy = acc[typeName] as TypePolicy | undefined;

      if (registeredTypePolicy) {
        if (hasConflict(mfeTypePolicy, registeredTypePolicy)) {
          // eslint-disable-next-line no-console
          console.error(`Type policy conflict for type '${typeName}'.`);
          return;
        }

        acc[typeName] = mergeTypePolicies(mfeTypePolicy, registeredTypePolicy);
      } else {
        acc[typeName] = mfeTypePolicy;
      }
    });

    return acc;
  }, {});

  return typePolicyRegistry;
};

const hasConflict = (typePolicy1: TypePolicy, typePolicy2: TypePolicy) => {
  const fields1 = typePolicy1.fields || {};
  const fields2 = typePolicy2.fields || {};

  return Object.keys(fields2).some(
    (fieldName) =>
      Object.hasOwn(fields1, fieldName) && Object.hasOwn(fields2, fieldName),
  );
};

const mergeTypePolicies = (
  typePolicy1: TypePolicy,
  typePolicy2: TypePolicy,
) => {
  return {
    ...typePolicy1,
    ...typePolicy2,
    fields: {
      ...typePolicy1.fields,
      ...typePolicy2.fields,
    },
  };
};
